import { useEffect, type ReactNode } from "react";

export type DrawerSize = "sm" | "md" | "lg" | "xl";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: DrawerSize;
}

const SIZES: Record<DrawerSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

export function Drawer({ open, onClose, title, description, children, footer, size = "md" }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div className={`no-print fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-foreground-950/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`absolute inset-y-0 right-0 flex w-full ${SIZES[size]} flex-col overflow-hidden border-l border-background-200 bg-background-50 shadow-2xl transition-transform duration-200 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-background-100 px-5 py-4">
          <div>
            {title ? <h2 className="text-base font-semibold text-foreground-900">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-foreground-600">{description}</p> : null}
          </div>
            <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer ? <div className="flex items-center justify-end gap-2 border-t border-background-100 px-5 py-3.5">{footer}</div> : null}
      </div>
    </div>
  );
}